import React from 'react';
import { Provider } from 'react-redux';
import {
  ConvexBetterAuthProvider,
  type AuthClient,
} from '@convex-dev/better-auth/react';
import { ErrorBoundary } from 'magical-trails-react';

import AppRouter from './AppRouter';
import store from './store';
import { convex } from './lib/convexClient';
import { authClient } from './lib/authClient';
import UpdateAvailableBanner from './components/UpdateAvailableBanner';

/**
 * The trail's base route for the current URL (`/trail/<code>`), so a crash
 * deep in a page can offer a way back to the map rather than only a reload
 * of the crashing URL. Progress lives in localStorage, so a fresh load of the
 * trail route resumes where the player was.
 */
export function trailHomeHref(pathname: string): string {
  const m = /^\/trail\/[^/]+/.exec(pathname);
  return m ? `${m[0]}/` : '/';
}

const AppCrashFallback: React.FC = () => (
  <div className="start-screen flex h-screen flex-col items-center justify-center gap-6 bg-cover bg-center bg-no-repeat">
    <div className="bg-mt-surface flex w-72 flex-col gap-4 rounded-2xl p-6 text-center shadow-2xl">
      <h1 className="text-mt-ink-soft text-xl font-bold tracking-tight">
        Something went wrong
      </h1>
      <p className="text-mt-ink-muted text-sm">
        Sorry — the trail hit a snag. Reloading usually fixes it.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="mt-btn bg-mt-primary text-mt-on-primary hover:bg-mt-primary-hover focus-visible:outline-mt-primary inline-flex items-center justify-center gap-x-2 rounded-md px-3.5 py-2.5 text-sm font-semibold shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        Reload
      </button>
      <a
        href={trailHomeHref(window.location.pathname)}
        className="text-mt-primary hover:text-mt-primary text-sm font-semibold"
      >
        Back to the trail map
      </a>
    </div>
  </div>
);

const App: React.FC = () => {
  // Open trails never need Convex/auth; when VITE_CONVEX_URL is unset (CI) or
  // Convex is unavailable, the app still renders and plays open trails.
  const tree = (
    <Provider store={store}>
      <AppRouter />
      <UpdateAvailableBanner />
    </Provider>
  );

  return (
    <div className="App">
      <a
        href="#main-content"
        className="focus:mt-btn bg-mt-primary focus:text-mt-on-primary sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:shadow-lg"
      >
        Skip to main content
      </a>
      <ErrorBoundary fallback={<AppCrashFallback />}>
        {convex ? (
          // better-auth's plugin generics don't line up structurally with the
          // provider's AuthClient union across minor versions; the runtime
          // shape is correct (crossDomain + convex client plugins), so cast.
          <ConvexBetterAuthProvider
            client={convex}
            authClient={authClient as unknown as AuthClient}
          >
            {tree}
          </ConvexBetterAuthProvider>
        ) : (
          tree
        )}
      </ErrorBoundary>
    </div>
  );
};

export default App;
